
import { useState, useEffect, useRef, useContext, createContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { SubjectSelector, SubjectTypeSelector, ProgramSelector, FeatureTypeSelector } from "@Beasted1010/pivotal-wisdom";
import { fetchSubjectData, fetchProgramFeatureData, fetchAllFeaturesWithProgressForProgram, fetchUserProgress } from "../utilities/server-fetch";
import { fetchUpdateUserProgress } from "../utilities/server-update";

import { LessonsContextProvider } from "./LessonsContext";

import { CourseContextProvider } from "./CourseContext";
import { DeepDivesContextProvider } from "./DeepDivesContext";

import { useSubjectsMetadata } from "./SubjectsMetadataContext";

import { useUser } from "./UserContext";

import { toKebabCase } from "../utilities/helpers";



export {
  ProgramContextProvider,
  useProgram, useProgramUpdate
};


const ProgramContext = createContext<any>( null ); // TODO: Properly fill out
const ProgramUpdateContext = createContext<any>( null );


function useProgram() {
  return useContext( ProgramContext );
}

function useProgramUpdate() {
  return useContext( ProgramUpdateContext );
}






type ProgramContextProps = {
  children?: JSX.Element | JSX.Element[];
  subjectType: SubjectTypeSelector;
  subjectSelector: SubjectSelector;
  programSelector: ProgramSelector;
  featureSelector: FeatureTypeSelector;
}


// TODO: Rename to SubjectContext ??? - But it does only have subjectData out of necessity to get programData
function ProgramContextProvider( {subjectType, subjectSelector, programSelector, featureSelector, children}: ProgramContextProps ) {
  // Provider handles...

  // ... Creating State
  const navigate = useNavigate();

  const [loading, setLoading] = useState( true );

  const [selectedSubject, setSelectedSubject] = useState<SubjectSelector>( subjectSelector );
  const [selectedProgram, setSelectedProgram] = useState<ProgramSelector>( programSelector );
  const [selectedFeature, setSelectedFeature] = useState<FeatureTypeSelector>( featureSelector );

  const [ selectedFeatureProgress, setSelectedFeatureProgress ] = useState<any>(); // TODO: TYPE

  // TODO ---- Do I really want this data retrieved/stored like this? Will WrappedComponents use?
  // TODO: Fetch Subject Data -- From SERVER instead of Mock in /data Dir
  // TODO: Determine EXACTLY what need, and see if can get ri dof some (i.e. derive others, like featureData, from what's already there)
  const [subjectData, setSubjectData] = useState<ISubject | undefined>( undefined );
  const [programData, setProgramData] = useState<IProgram | undefined>( undefined );
  const [featuresData, setFeaturesData] = useState<IFeature[] | undefined>( undefined );
  const [userProgress, setUserProgress] = useState<any | undefined>( undefined ); // TODO: TYPE

  const [featureContent, setFeatureContent] = useState<ContentType>( null );

  const { foundationSubjectsMetadata, specialtySubjectsMetadata } = useSubjectsMetadata();

  const { user } = useUser();

  //const prevProps = useRef( {user} ); // Used to get insight into what changed to trigger useEffect

  // ... Updating State
  useEffect( () => {
    const featureProgress = userProgress?.find( (progress: any) => {
      return progress.featureSelector == selectedFeature;
    });

    setSelectedFeatureProgress( featureProgress?.progress?.lessons );

  }, [selectedFeature, userProgress] );

  useEffect( () => {
    setSelectedSubject( subjectSelector );
    setSelectedProgram( programSelector );

    const abortController: AbortController = new AbortController();
    async function updateData() {

      if( foundationSubjectsMetadata == null || specialtySubjectsMetadata == null ) return;

      let newSubjectData;
      switch( subjectType ) {
        case SubjectTypeSelector.FOUNDATION: {
          // TODO: Consider better way (so not going thorugh Subjects one by one via ".find" -- Consider converting to Object or Map of some sort, but need update Ribbon to use appropriately)
          newSubjectData = foundationSubjectsMetadata.find( (subjectMetadata: ISubjectMetadata) => {
            return subjectMetadata.selector == subjectSelector;
          });

          break;
        }

        case SubjectTypeSelector.SPECIALTY: {
          // TODO: Consider better way (so not going thorugh Subjects one by one via ".find" -- Consider converting to Object or Map of some sort, but need update Ribbon to use appropriately)
          newSubjectData = specialtySubjectsMetadata.find( (subjectMetadata: ISubjectMetadata) => {
            return subjectMetadata.selector == subjectSelector;
          });

          break;
        }

        default: {
          // TODO: ERROR Handle
          console.error(`Unknown Subject Type: ${subjectType}`);
        }
      }

      setSubjectData( newSubjectData );

      const newProgramData = newSubjectData?.programs.find( (program: IProgram) => {
        return program.selector == programSelector;
      });
      setProgramData( newProgramData );

      /*const newFeaturesData = newProgramData?.features.find( (feature: IFeature) => {
        return feature.selector = featureSelector;
      });*/
      let newFeaturesData = featuresData;
      if( featuresData == null || programSelector != selectedProgram ) {
        // TODO: PERFORMANCE - Retrieves from Server every time change any in Dependency Array below... (assumign Conditions here are met) -- Consider CACHING
        const featuresAndProgress = await getAllProgramFeaturesWithProgressFromServer( abortController.signal );
        setFeaturesData( featuresAndProgress?.featuresData );
        setUserProgress( featuresAndProgress?.userProgress );
      }

      //if( newFeaturesData != null ) {
        //selectFeature( featureSelector );//, newFeaturesData );
      //}
      // BELOW REPLACED ABOVE IF STATEMENT (experimenting)
      const feature = newFeaturesData?.find( (featureData) => {
        return featureData.selector === featureSelector;
      });
      setSelectedFeature( featureSelector );
      setFeatureContent( feature?.content );

      setLoading( false );
    }

    updateData();

    return () => {
      abortController.abort(); // TODO: Don't think this is doing anything now since getAllProgramFeaturesFromServer() is handling abort only if Exception... maybe pass abortController to method (will that work, pass by Ref?) ??
    }
    // TODO: Will "Loading" as part of Dependency Array cause repeated renders?
  }, [foundationSubjectsMetadata, specialtySubjectsMetadata, subjectType, subjectSelector, programSelector, featureSelector, loading]);

  useEffect( () => {
    // TODO: Seems to be called, but only after a page refresh once login...
    async function updateData() {
      const abortController: AbortController = new AbortController();

      const featuresAndProgress = await getAllProgramFeaturesWithProgressFromServer( abortController.signal );

      if( featuresAndProgress != null ) {
        setFeaturesData( featuresAndProgress.featuresData );
        setUserProgress( featuresAndProgress.userProgress );
      }

      if( featuresAndProgress != null ) {
        selectFeature( featureSelector, featuresAndProgress.featuresData );
      }

      return () => {
        abortController.abort();
      }
    }

    updateData();

  }, [user]);


  function selectFeature( featureToSelect: FeatureTypeSelector, newFeaturesData: IFeature[] | undefined = featuresData ) { // TODO: New approach may not need second param for FeaturesData since one at a time
    //if( featuresData == null ) return;
    if( !newFeaturesData ) newFeaturesData = featuresData;
    /* ... Currently doesn't seem like even using newFeaturesData
    if( newFeaturesData == null ) {
      console.error(`ERROR: Expected Features Data to be populated, but it was null`);
      return;
    }
    */

    
    /*
    getSelectedFeatureFromServer( featureToSelect ).then( (featureData: IFeature | undefined) => {
      if( !featureData ) {
        console.error(`Feature Data is not available. Selected ${featureToSelect}`);
        return;
      }

      const featureContent: ContentType = featureData.content;
      setSelectedFeature( featureToSelect );
      setFeatureContent( featureContent );
      //setFeaturesData( [featureData] ); // TODO: May not need be an Array anymore...

      console.log( "NAVIGATING AGAIN..." );
      navigate( `/content/${subjectType}/${subjectSelector}/${programSelector}/${featureToSelect}` );
    });
    */
    const feature = newFeaturesData?.find( (featureData) => {
      return featureData.selector === featureToSelect;
    });
    setSelectedFeature( featureSelector );
    setFeatureContent( feature?.content );
    setFeaturesData( newFeaturesData );
    // TODO: Currently just blindly adding toKebabCase to get consistency with URL -- Consider more efficient way to handle this or at least place earlier to have variable itself already be in Kebab Case
    navigate( `/content/${toKebabCase(subjectType)}/${toKebabCase(subjectSelector)}/${toKebabCase(programSelector)}/${toKebabCase(featureToSelect)}` );

    /*
    let feature = getSelectedFeature( featureToSelect, newFeaturesData );

    const featureContent: ContentType = feature.content;
    setSelectedFeature( featureToSelect );
    setFeatureContent( featureContent );

    navigate( `/content/${subjectType}/${subjectSelector}/${programSelector}/${featureToSelect}` );
    */
  }

  /*
  function getSelectedFeature( featureToSelect: FeatureTypeSelector, features: IFeature[] ) {
    // NOTE: BELOW IS OLD APPROACH WHEN RETRIEVED ALL DATA AT ONCE (now doing getSelectedFeatureFromServer)
    let feature: IFeature | undefined = features.find( (feature: IFeature) => {
      return feature.selector === featureToSelect;
    });

    if( feature == null ) {
      feature = features[0]; // Default to first Feature
    }

    return feature;
  }

  async function getSelectedFeatureFromServer( featureToSelect: FeatureTypeSelector, abortSignal?: AbortSignal ) {
    try {
      // TODO: Currently retrieving ALL Features for Subject/Program (later may want fetch Course and then Lazy Load others?)
      const featureData: IFeature = await fetchProgramFeatureData( {subjectSelector: subjectSelector, programSelector: programSelector, featureSelector: featureSelector, signal: abortSignal} );

      return featureData;
    } catch(error) {
      console.error(`ERROR fetching Feature Data for Feature ${featureToSelect}, given Subject=${subjectSelector} and Program=${programSelector}`, error);
      return;
    }
  }
  */

  async function getAllProgramFeaturesWithProgressFromServer( abortSignal: AbortSignal) {
    try {
      const featuresAndProgress = await fetchAllFeaturesWithProgressForProgram( {subjectSelector: subjectSelector, programSelector: programSelector, signal: abortSignal} );

      return featuresAndProgress;
    } catch(error) {
      console.error(`ERROR fetching Features Data, given Subject=${subjectSelector} and Program=${programSelector}`, error);
      return;
    }
  }


  const updateUserProgressForFeature = useCallback( async (featureUserProgress: any) => { // TODO: TYPE
    const updatedFeatureUserProgress = await fetchUpdateUserProgress( featureUserProgress );

    setUserProgress( (prevUserProgress: any | undefined) => {

      if( Array.isArray(prevUserProgress) ) {
        const featureUserProgressIndex = prevUserProgress.findIndex((progress: any) => {
          return progress.featureSelector === selectedFeature;
        });

        if( featureUserProgressIndex !== -1 ) {
          const updatedProgress = [...prevUserProgress];
          updatedProgress[featureUserProgressIndex] = updatedFeatureUserProgress;

          return updatedProgress;
        }

        prevUserProgress[featureUserProgressIndex] = updatedFeatureUserProgress;

        return [...prevUserProgress];
      }

      return prevUserProgress;
    });

  }, [user, selectedFeature]);

  



  // ... Persisting State down into Children
  return (
    <ProgramContext.Provider value={ {programData, featuresData, userProgress, selectedFeatureProgress, featureContent, selectedSubject, selectedProgram, selectedFeature} } >
      <ProgramUpdateContext.Provider value={ {selectFeature, updateUserProgressForFeature} } >

        {
          loading ?
            <h1>Loading...</h1>
          :
            /* TODO: This approach causes any Lesson Feature to save Lesson #, but if switch to other Feature, it looses it (cuz Context storing that data is removed) -- FIX INCONSISTENCY */
            /* ------- May need to ultimately surround ALL Features with this... May at least ensure it loads only once */
            featureContainsLessons(selectedFeature) ?
              // Things that use Lessons
              <LessonsContextProvider lessons={featureContent?.lessons} >
                { renderFeatureSwitch(selectedFeature, children) }
              </LessonsContextProvider>
            :
              renderFeatureSwitch(selectedFeature, children)
        }

      </ProgramUpdateContext.Provider>
    </ProgramContext.Provider>
  )
}


// ================================= HELPERS =================================
function featureContainsLessons( selectedFeature: FeatureTypeSelector ) {
  // TODO: Somehow sync with JSON? I.e. search JSON for "hasOwnProperty"? "lessons" Field??
  return (
    selectedFeature === FeatureTypeSelector.COURSE ||
    selectedFeature === FeatureTypeSelector.DEEP_DIVES
  );
}

function renderFeatureSwitch( selectedFeature: FeatureTypeSelector, children: JSX.Element | JSX.Element[] | undefined ) {
  switch( selectedFeature ) {
    case FeatureTypeSelector.COURSE:
      return (
        <CourseContextProvider >
          {children}
        </CourseContextProvider>
      );
      break;

    case FeatureTypeSelector.DEEP_DIVES:
      return (
        <DeepDivesContextProvider >
          {children}
        </DeepDivesContextProvider>
      );
      break;

    default:
      return <>{children}</>;
      break;
  }
}








/* */
